/**
 * Session Expiry Modal Component
 * 
 * Feature: 033-auth-complete (Phase 4 - User Story 2)
 * Description: Warning modal before session timeout
 * Constitution: v1.11.0
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { useDraggable } from '../ui/useDraggable';

interface SessionExpiryModalProps {
  show: boolean;
  remainingSeconds: number;
  onExtend: () => void;
  onLogout: () => void;
}

export const SessionExpiryModal: React.FC<SessionExpiryModalProps> = ({
  show,
  remainingSeconds,
  onExtend,
  onLogout,
}) => {
  const [countdown, setCountdown] = useState(remainingSeconds);
  const { transform, onHandleMouseDown } = useDraggable({ handleSelector: '[data-modal-drag-handle]' });

  useEffect(() => {
    if (!show) return;

    setCountdown(remainingSeconds);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [show, remainingSeconds]);

  if (!show) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div className="fixed inset-0 bg-transparent flex items-center justify-center z-50">
      <div
        onMouseDown={onHandleMouseDown}
        style={{ transform }}
        className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] shadow-2xl p-6 max-w-md w-full mx-4">
        <div data-modal-drag-handle className="flex items-center mb-4 cursor-move select-none">
          <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mr-4">
            <svg
              className="w-6 h-6 text-yellow-600 dark:text-yellow-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--color-text-primary)]">Session Expiring Soon</h2>
            <p className="text-sm text-[var(--color-text-secondary)]">Your session is about to expire</p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-[var(--color-text-secondary)] mb-4">
            You have been inactive for a while. Your session will expire in:
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-center">
            <div className="text-4xl font-bold text-yellow-600 dark:text-yellow-400">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">minutes remaining</p>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onExtend}
            className="flex-1 px-4 py-3 bg-[var(--color-accent)] text-white rounded-lg hover:opacity-90 transition-colors font-medium"
          >
            Continue Working
          </button>
          <button
            onClick={onLogout}
            className="flex-1 px-4 py-3 bg-[var(--color-bg-subtle)] text-[var(--color-text-primary)] rounded-lg hover:bg-[var(--color-border)] transition-colors font-medium"
          >
            Sign Out
          </button>
        </div>

        <p className="text-xs text-[var(--color-text-secondary)] text-center mt-4">
          Click "Continue Working" to extend your session, or "Sign Out" to end it now.
        </p>
      </div>
    </div>
  );
};
